import { useEffect, useMemo, useState } from "react";
import { AppShell } from "@/components/layout/AppShell";
import { PageHeader } from "@/components/layout/PageHeader";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { Card, CardContent } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { useBusiness } from "@/hooks/use-business";
import { useToast } from "@/hooks/use-toast";
import { useServices } from "@/hooks/use-services";
import { useServiceCategories } from "@/hooks/use-service-categories";
import { cn } from "@/lib/utils";
import { themeVarsFromHex } from "@/lib/theme";
import { supabase } from "@/integrations/supabase/client";
import { getBaseUrl } from "@/lib/site";
import {
  Battery,
  Check,
  ChevronLeft,
  Copy,
  ExternalLink,
  Globe,
  QrCode,
  Signal,
  Wifi,
  Plus,
  Minus,
  Palette,
  FileText,
} from "lucide-react";

function qrUrl(url: string) {
  return `https://api.qrserver.com/v1/create-qr-code/?size=220x220&data=${encodeURIComponent(url)}`;
}

type PreviewStep = "menu" | "details" | "confirm" | "success";

function FoodOrderIPhonePreview({
  themeStyle,
  businessName,
  logoUrl,
  welcomeMessage,
  currency,
  categories,
  items,
}: {
  themeStyle?: React.CSSProperties;
  businessName: string;
  logoUrl?: string | null;
  welcomeMessage?: string | null;
  currency: string;
  categories: Array<{ id: string; name: string }>;
  items: Array<{
    id: string;
    name: string;
    description?: string | null;
    price_amount: number;
    currency?: string | null;
    category_id?: string | null;
    image_url?: string | null;
    media_items?: any;
  }>;
}) {
  const primaryMediaFor = (it: any) => {
    const raw = (it as any)?.media_items;
    if (Array.isArray(raw) && raw.length > 0) {
      const first = raw[0];
      const url = String(first?.url || "").trim();
      const t = String(first?.type || "").toLowerCase();
      if (url) return { url, type: t === "video" ? "video" : "image" } as const;
    }
    const url = String((it as any)?.image_url || "").trim();
    if (!url) return null;
    const lower = url.toLowerCase();
    const type =
      lower.endsWith(".mp4") ||
      lower.endsWith(".webm") ||
      lower.endsWith(".mov") ||
      lower.endsWith(".avi") ||
      lower.endsWith(".mkv")
        ? "video"
        : "image";
    return { url, type } as const;
  };

  const [step, setStep] = useState<PreviewStep>("menu");
  const [selectedCategory, setSelectedCategory] = useState<"all" | "uncategorized" | string>("all");
  const [cart, setCart] = useState<Record<string, number>>({});

  const displayItems = useMemo(() => {
    if (selectedCategory === "all") return items;
    if (selectedCategory === "uncategorized") return items.filter((i) => !i.category_id);
    return items.filter((i) => String(i.category_id) === String(selectedCategory));
  }, [items, selectedCategory]);

  const total = useMemo(() => {
    let t = 0;
    for (const it of items) {
      const qty = Number(cart[it.id] || 0);
      if (qty > 0) t += Number(it.price_amount || 0) * qty;
    }
    return t;
  }, [cart, items]);

  const steps: PreviewStep[] = ["menu", "details", "confirm"];
  const currentStepIndex = steps.indexOf(step);

  const goBack = () => {
    if (step === "success") {
      setStep("menu");
      setCart({});
      setSelectedCategory("all");
      return;
    }
    const prevIndex = currentStepIndex - 1;
    if (prevIndex >= 0) setStep(steps[prevIndex]);
  };

  const setQty = (id: string, qty: number) => {
    setCart((prev) => {
      const next = { ...prev };
      if (qty <= 0) {
        delete next[id];
        return next;
      }
      next[id] = qty;
      return next;
    });
  };

  const cartCount = Object.values(cart).reduce((s, n) => s + Number(n || 0), 0);

  return (
    <div className="flex justify-center">
      <div className="relative">
        <div className="bg-gradient-to-b from-[#3a3a3c] to-[#2c2c2e] rounded-[3rem] p-[3px] shadow-2xl">
          <div className="bg-background rounded-[2.8rem] overflow-hidden w-[280px] sm:w-[300px] relative">
            <div className="bg-background px-6 pt-3 pb-1 flex items-center justify-between relative z-20">
              <span className="text-xs font-medium text-foreground">9:41</span>
              <div className="absolute left-1/2 -translate-x-1/2 top-2">
                <div className="w-[90px] h-[28px] bg-black rounded-full flex items-center justify-center gap-2">
                  <div className="w-[10px] h-[10px] rounded-full bg-[#1a1a1a] border border-[#2a2a2a]" />
                </div>
              </div>
              <div className="flex items-center gap-1">
                <Signal className="h-3.5 w-3.5 text-foreground" />
                <Wifi className="h-3.5 w-3.5 text-foreground" />
                <Battery className="h-4 w-4 text-foreground" />
              </div>
            </div>

            <div className="h-[520px] sm:h-[560px] overflow-y-auto bg-background" style={themeStyle}>
              {step !== "success" && (
                <div className="sticky top-0 bg-card/95 backdrop-blur-sm border-b border-border px-4 py-3 z-10 flex items-center gap-3">
                  {step !== "menu" && (
                    <button onClick={goBack} className="text-primary">
                      <ChevronLeft className="h-5 w-5" />
                    </button>
                  )}
                  <h1 className="font-semibold text-foreground text-sm flex-1">
                    {step === "menu" && "Order Food"}
                    {step === "details" && "Your Details"}
                    {step === "confirm" && "Confirm Order"}
                  </h1>
                </div>
              )}

              <div className="p-4">
                {step === "menu" && (
                  <div className="text-center mb-5">
                    {logoUrl ? (
                      <img
                        src={String(logoUrl)}
                        alt={`${businessName} logo`}
                        className="h-10 w-10 mx-auto mb-3 rounded-full object-cover border-2 border-border"
                        onError={(e) => {
                          const target = e.target as HTMLImageElement;
                          target.style.display = "none";
                          const fallback = target.nextElementSibling as HTMLElement;
                          if (fallback) fallback.style.display = "flex";
                        }}
                      />
                    ) : null}
                    <div
                      className={cn(
                        "h-10 w-10 rounded-full mx-auto mb-3 flex items-center justify-center font-bold text-sm",
                        "bg-primary text-primary-foreground",
                        logoUrl ? "hidden" : "",
                      )}
                    >
                      {businessName.substring(0, 2).toUpperCase()}
                    </div>
                    <h2 className="text-base font-bold text-foreground">{businessName}</h2>
                    <p className="text-muted-foreground text-xs mt-1">
                      {welcomeMessage && String(welcomeMessage).trim().length > 0 ? String(welcomeMessage) : "Select items and checkout."}
                    </p>
                  </div>
                )}

                {step !== "success" && (
                  <div className="flex items-center justify-center gap-1.5 mb-5">
                    {steps.map((s, i) => (
                      <div key={s} className="flex items-center">
                        <div className={cn("h-2 w-2 rounded-full transition-colors", i <= currentStepIndex ? "bg-primary" : "bg-muted")} />
                        {i < steps.length - 1 && (
                          <div className={cn("w-6 h-0.5 mx-0.5 transition-colors", i < currentStepIndex ? "bg-primary" : "bg-muted")} />
                        )}
                      </div>
                    ))}
                  </div>
                )}

                {step === "menu" && (
                  <div className="space-y-3">
                    {/* Category chips */}
                    {(categories.length > 0 || items.some((i) => !i.category_id)) && (
                      <div className="flex gap-2 overflow-x-auto pb-1 -mx-1 px-1">
                        <button
                          type="button"
                          onClick={() => setSelectedCategory("all")}
                          className={cn(
                            "px-3 py-1.5 rounded-full text-xs font-medium whitespace-nowrap border transition-colors",
                            selectedCategory === "all" ? "bg-primary text-primary-foreground border-primary" : "bg-background border-border text-muted-foreground",
                          )}
                        >
                          All
                        </button>
                        {items.some((i) => !i.category_id) ? (
                          <button
                            type="button"
                            onClick={() => setSelectedCategory("uncategorized")}
                            className={cn(
                              "px-3 py-1.5 rounded-full text-xs font-medium whitespace-nowrap border transition-colors",
                              selectedCategory === "uncategorized"
                                ? "bg-primary text-primary-foreground border-primary"
                                : "bg-background border-border text-muted-foreground",
                            )}
                          >
                            Other
                          </button>
                        ) : null}
                        {categories.map((c) => (
                          <button
                            key={c.id}
                            type="button"
                            onClick={() => setSelectedCategory(String(c.id))}
                            className={cn(
                              "px-3 py-1.5 rounded-full text-xs font-medium whitespace-nowrap border transition-colors",
                              String(selectedCategory) === String(c.id)
                                ? "bg-primary text-primary-foreground border-primary"
                                : "bg-background border-border text-muted-foreground",
                            )}
                          >
                            {c.name}
                          </button>
                        ))}
                      </div>
                    )}

                    <div className="rounded-xl border border-border bg-card p-3">
                      <div className="flex items-center justify-between text-xs">
                        <span className="text-muted-foreground">Cart</span>
                        <span className="font-medium text-foreground">{cartCount} item(s)</span>
                      </div>
                      <div className="mt-1 flex items-center justify-between text-sm">
                        <span className="font-medium text-foreground">Total</span>
                        <span className="font-semibold text-foreground">{currency} {total.toFixed(2)}</span>
                      </div>
                    </div>

                    <div className="space-y-2">
                      {displayItems.slice(0, 8).map((it) => {
                        const qty = Number(cart[it.id] || 0);
                        const ccy = it.currency || currency;
                        const m = primaryMediaFor(it);
                        return (
                          <div key={it.id} className="rounded-xl border border-border bg-card p-3">
                            <div className="flex items-start justify-between gap-3">
                              <div className="flex items-start gap-3 min-w-0 flex-1">
                                {m?.url ? (
                                  m.type === "video" ? (
                                    <video
                                      src={m.url}
                                      className="h-14 w-14 rounded-lg object-cover border border-border bg-muted flex-shrink-0"
                                      muted
                                      playsInline
                                      preload="metadata"
                                    />
                                  ) : (
                                    <img
                                      src={m.url}
                                      alt={String(it.name || "Item")}
                                      className="h-14 w-14 rounded-lg object-cover border border-border bg-muted flex-shrink-0"
                                      loading="lazy"
                                    />
                                  )
                                ) : (
                                  <div className="h-14 w-14 rounded-lg border border-border bg-muted flex items-center justify-center text-xs font-semibold text-muted-foreground flex-shrink-0">
                                    {String(it.name || "M").slice(0, 1).toUpperCase()}
                                  </div>
                                )}

                                <div className="min-w-0">
                                  <div className="text-sm font-semibold text-foreground truncate">{it.name}</div>
                                  {it.description ? <div className="text-xs text-muted-foreground line-clamp-2 mt-0.5">{it.description}</div> : null}
                                  <div className="text-sm font-semibold text-foreground mt-3">{ccy} {Number(it.price_amount || 0).toFixed(2)}</div>
                                </div>
                              </div>
                              <div className="flex items-center gap-2">
                                <button
                                  type="button"
                                  className="h-7 w-7 rounded-full border border-border flex items-center justify-center text-muted-foreground"
                                  onClick={() => setQty(it.id, qty - 1)}
                                  disabled={qty <= 0}
                                >
                                  <Minus className="h-3.5 w-3.5" />
                                </button>
                                <div className="w-6 text-center text-sm font-semibold">{qty}</div>
                                <button
                                  type="button"
                                  className="h-7 w-7 rounded-full border border-border flex items-center justify-center text-primary"
                                  onClick={() => setQty(it.id, qty + 1)}
                                >
                                  <Plus className="h-3.5 w-3.5" />
                                </button>
                              </div>
                            </div>
                          </div>
                        );
                      })}
                      {displayItems.length > 8 ? (
                        <div className="text-xs text-muted-foreground text-center">…and more items</div>
                      ) : null}
                    </div>

                    <Button className="w-full" onClick={() => setStep("details")} disabled={cartCount === 0}>
                      Continue
                    </Button>
                  </div>
                )}

                {step === "details" && (
                  <div className="space-y-3">
                    <div className="rounded-xl border border-border bg-card p-3 space-y-3">
                      <div className="space-y-1">
                        <Label className="text-xs">Name</Label>
                        <Input placeholder="Your name" />
                      </div>
                      <div className="space-y-1">
                        <Label className="text-xs">Phone</Label>
                        <Input placeholder="055..." />
                      </div>
                      <div className="space-y-1">
                        <Label className="text-xs">Delivery address</Label>
                        <Input placeholder="Address (optional)" />
                      </div>
                    </div>
                    <div className="flex gap-2">
                      <Button variant="outline" className="w-full" onClick={() => setStep("menu")}>
                        Back
                      </Button>
                      <Button className="w-full" onClick={() => setStep("confirm")}>
                        Review
                      </Button>
                    </div>
                  </div>
                )}

                {step === "confirm" && (
                  <div className="space-y-3">
                    <div className="rounded-xl border border-border bg-card p-3">
                      <div className="text-sm font-semibold text-foreground">Order summary</div>
                      <div className="mt-2 space-y-1">
                        {Object.entries(cart).slice(0, 4).map(([id, qty]) => {
                          const it = items.find((x) => x.id === id);
                          if (!it) return null;
                          return (
                            <div key={id} className="flex items-center justify-between text-xs">
                              <span className="text-foreground truncate">{qty}× {it.name}</span>
                              <span className="text-muted-foreground">{currency} {(Number(it.price_amount || 0) * Number(qty || 0)).toFixed(2)}</span>
                            </div>
                          );
                        })}
                        <div className="mt-2 pt-2 border-t border-border flex items-center justify-between text-sm">
                          <span className="font-medium">Total</span>
                          <span className="font-semibold">{currency} {total.toFixed(2)}</span>
                        </div>
                      </div>
                    </div>
                    <div className="flex gap-2">
                      <Button variant="outline" className="w-full" onClick={() => setStep("details")}>
                        Back
                      </Button>
                      <Button className="w-full" onClick={() => setStep("success")}>
                        Place order
                      </Button>
                    </div>
                  </div>
                )}

                {step === "success" && (
                  <div className="text-center space-y-3 py-6">
                    <div className="mx-auto h-12 w-12 rounded-full bg-primary/10 flex items-center justify-center">
                      <Check className="h-6 w-6 text-primary" />
                    </div>
                    <div className="text-base font-semibold text-foreground">Order received</div>
                    <div className="text-xs text-muted-foreground">This is a preview — no real order was placed.</div>
                    <Button variant="outline" onClick={goBack}>Start over</Button>
                  </div>
                )}
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}

export default function OrdersPagePreview() {
  const { business, loading: businessLoading, refetch } = useBusiness();
  const { toast } = useToast();
  const { services, loading: servicesLoading } = useServices();
  const { categories: serviceCategories, loading: categoriesLoading } = useServiceCategories();

  const [copied, setCopied] = useState(false);
  const [isSaving, setIsSaving] = useState(false);
  const [settings, setSettings] = useState({
    primaryColor: "#2563EB",
    logoUrl: "",
    welcomeMessage: "Order online, anytime",
    orderPolicy: "",
    termsAndConditions: "",
  });

  const previewThemeStyle = useMemo(() => themeVarsFromHex(settings.primaryColor), [settings.primaryColor]);

  const origin = getBaseUrl();
  // IMPORTANT:
  // useBusiness() can transiently return business=null during refetch. If we derive the iframe src directly
  // from business.slug, it will flip "" <-> url and the iframe will look like it's reloading/glitching.
  // So we keep the last known slug stable.
  const [stableBusinessSlug, setStableBusinessSlug] = useState<string>("");
  const [stableBusinessName, setStableBusinessName] = useState<string>("");
  useEffect(() => {
    const slug = (business as any)?.slug ? String((business as any).slug) : "";
    if (slug) setStableBusinessSlug(slug);
    const name = (business as any)?.name ? String((business as any).name) : "";
    if (name) setStableBusinessName(name);
  }, [(business as any)?.slug, (business as any)?.name]);

  // Keep customization settings in sync with saved business data
  useEffect(() => {
    if (!business) return;
    setSettings((prev) => ({
      ...prev,
      primaryColor: (business as any)?.primary_color || prev.primaryColor,
      logoUrl: (business as any)?.logo_url || prev.logoUrl,
      welcomeMessage: business?.description || prev.welcomeMessage,
      orderPolicy: (business as any)?.cancellation_policy || prev.orderPolicy,
      termsAndConditions: (business as any)?.terms_and_conditions || prev.termsAndConditions,
    }));
  }, [business]);

  // New (non-weekly) public ordering page for catering businesses
  const ordersUrl = useMemo(() => {
    if (!origin || !stableBusinessSlug) return "";
    return `${origin}/order/${stableBusinessSlug}`;
  }, [origin, stableBusinessSlug]);

  const copyText = async (text: string, message = "Copied") => {
    try {
      await navigator.clipboard.writeText(text);
      toast({ title: message });
    } catch {
      toast({ title: "Copy failed", variant: "destructive" });
    }
  };

  const handleCopyLink = async () => {
    if (!ordersUrl) return;
    await navigator.clipboard.writeText(ordersUrl);
    setCopied(true);
    setTimeout(() => setCopied(false), 1200);
  };

  // Match the appointment booking preview behavior:
  // Only show a blocking loader on the *first* load (when we have no stable slug yet).
  // During background refetches, keep rendering so the iframe doesn't unmount/remount (which looks like "glitching").
  if (businessLoading && !stableBusinessSlug) {
    return (
      <AppShell businessName="Loading...">
        <div className="flex items-center justify-center py-12 text-sm text-muted-foreground">Loading…</div>
      </AppShell>
    );
  }

  const handleSaveChanges = async () => {
    if (!business) {
      toast({ title: "No business found", variant: "destructive" });
      return;
    }

    // Validate primary color
    if (!/^#[0-9A-Fa-f]{6}$/.test(settings.primaryColor.trim())) {
      toast({
        title: "Invalid color",
        description: "Please enter a valid hex color (e.g., #2563EB)",
        variant: "destructive",
      });
      return;
    }

    setIsSaving(true);
    try {
      const updateData: any = {
        primary_color: settings.primaryColor.trim(),
        logo_url: settings.logoUrl.trim() || null,
        description: settings.welcomeMessage.trim() || null,
        // Reuse the existing business fields (shared across modules)
        cancellation_policy: settings.orderPolicy.trim() || null,
        terms_and_conditions: settings.termsAndConditions.trim() || null,
      };

      const { error } = await supabase.from("businesses").update(updateData).eq("id", (business as any).id);
      if (error) throw error;

      await refetch();
      toast({ title: "Saved", description: "Orders page settings updated successfully." });
    } catch (e: any) {
      toast({ title: "Save failed", description: e?.message || "Please try again.", variant: "destructive" });
    } finally {
      setIsSaving(false);
    }
  };

  return (
    <AppShell businessName={stableBusinessName || business?.name || "My Business"}>
      <PageHeader
        title="Orders Page"
        description="Preview and share your public food ordering page"
      />

      <Tabs defaultValue="preview" className="space-y-4 sm:space-y-6">
        <TabsList className="glass-card w-full sm:w-auto overflow-x-auto">
          <TabsTrigger value="preview" className="flex-1 sm:flex-none">
            <Globe className="h-4 w-4 mr-2 hidden sm:inline" />
            Preview
          </TabsTrigger>
          <TabsTrigger value="share" className="flex-1 sm:flex-none">
            <ExternalLink className="h-4 w-4 mr-2 hidden sm:inline" />
            Share
          </TabsTrigger>
          <TabsTrigger value="customize" className="flex-1 sm:flex-none">
            <Palette className="h-4 w-4 mr-2 hidden sm:inline" />
            Customize
          </TabsTrigger>
        </TabsList>

        <TabsContent value="preview" className="space-y-4 sm:space-y-6">
          <div className="glass-card p-4 space-y-4">
            <div className="grid gap-3 sm:grid-cols-[1fr_auto] sm:items-end">
              <div className="flex gap-2">
                <Button variant="outline" onClick={handleCopyLink} disabled={!ordersUrl} className="flex-1 sm:flex-none">
                  {copied ? <Check className="h-4 w-4 mr-2" /> : <Copy className="h-4 w-4 mr-2" />}
                  {copied ? "Copied" : "Copy"}
                </Button>
                <Button asChild disabled={!ordersUrl} className="flex-1 sm:flex-none">
                  <a href={ordersUrl || "#"} target="_blank" rel="noreferrer">
                    <ExternalLink className="h-4 w-4 mr-2" />
                    Open
                  </a>
                </Button>
              </div>
            </div>

            <div className="space-y-2">
              <Label>Your orders page link</Label>
              <Input value={ordersUrl || ""} readOnly placeholder="Select a company link to see the URL" className="font-mono text-xs" />
            </div>
          </div>

          <Card className="border border-border">
            <CardContent className="p-4 sm:p-8">
              <div className="mb-4 flex items-center justify-center gap-2">
                <span className="text-xs text-muted-foreground">Interactive Preview • Tap to navigate</span>
              </div>
              <FoodOrderIPhonePreview
                themeStyle={previewThemeStyle}
                businessName={stableBusinessName || business?.name || "Demo Business"}
                logoUrl={settings.logoUrl || (business as any)?.logo_url || null}
                welcomeMessage={settings.welcomeMessage || business?.description || null}
                currency={business?.currency || "GHS"}
                categories={(serviceCategories || []).filter((c: any) => c.active).map((c: any) => ({ id: String(c.id), name: String(c.name) }))}
                items={(services || []).filter((s: any) => s.active).map((s: any) => ({
                  id: String(s.id),
                  name: String(s.name),
                  description: s.description ? String(s.description) : null,
                  price_amount: Number(s.price_amount || 0),
                  currency: s.currency ? String(s.currency) : null,
                  category_id: s.category_id ? String(s.category_id) : null,
                  image_url: s.image_url ? String(s.image_url) : null,
                  media_items: (s as any)?.media_items ?? null,
                }))}
              />
              {servicesLoading || categoriesLoading ? (
                <div className="mt-4 text-xs text-muted-foreground text-center">Loading menu items…</div>
              ) : null}
            </CardContent>
          </Card>
        </TabsContent>

        <TabsContent value="share" className="space-y-4 sm:space-y-6">
          <div className="glass-card p-4 sm:p-6 space-y-4">
            <div>
              <h3 className="font-semibold text-foreground">Share your Orders Page</h3>
              <p className="text-sm text-muted-foreground">
                Pick a company link, then copy the URL, share a QR code, or embed it.
              </p>
            </div>

            <div className="flex flex-col sm:flex-row gap-3">
              <Input value={ordersUrl || ""} readOnly className="flex-1 font-mono text-xs" placeholder="Select a company link first" />
              <Button onClick={() => copyText(ordersUrl, "Link copied")} disabled={!ordersUrl} className="w-full sm:w-auto">
                <Copy className="h-4 w-4 mr-2" />
                Copy Link
              </Button>
            </div>

            <div className="grid grid-cols-1 lg:grid-cols-2 gap-3 sm:gap-4 pt-4 border-t border-border">
              <div className="rounded-xl border border-border bg-background/60 p-4">
                <div className="flex items-center gap-2 mb-3">
                  <QrCode className="h-4 w-4 text-muted-foreground" />
                  <p className="text-sm font-semibold text-foreground">QR Code</p>
                </div>
                {ordersUrl ? (
                  <div className="flex items-center justify-center">
                    <img
                      src={qrUrl(ordersUrl)}
                      alt="Orders page QR code"
                      className="h-[200px] w-[200px] rounded-lg border border-border bg-white p-2"
                      loading="lazy"
                    />
                  </div>
                ) : (
                  <div className="text-sm text-muted-foreground">Select a company link to generate a QR code.</div>
                )}
              </div>

              <div className="rounded-xl border border-border bg-background/60 p-4">
                <div className="flex items-center gap-2 mb-3">
                  <Copy className="h-4 w-4 text-muted-foreground" />
                  <p className="text-sm font-semibold text-foreground">Embed Widget</p>
                </div>
                <p className="text-xs text-muted-foreground mb-3">
                  Paste this on your website to embed your orders page (per company link).
                </p>
                <div className="rounded-lg border border-border bg-muted/40 p-3">
                  <code className="text-[11px] leading-4 text-foreground break-all">
                    {ordersUrl
                      ? `<iframe src="${ordersUrl}" style="width:100%;min-height:780px;border:0;border-radius:12px;" loading="lazy"></iframe>`
                      : "Select a company link first"}
                  </code>
                </div>
                <div className="mt-3">
                  <Button
                    className="w-full"
                    variant="outline"
                    disabled={!ordersUrl}
                    onClick={() =>
                      copyText(
                        `<iframe src="${ordersUrl}" style="width:100%;min-height:780px;border:0;border-radius:12px;" loading="lazy"></iframe>`,
                        "Embed code copied",
                      )
                    }
                  >
                    <Copy className="h-4 w-4 mr-2" />
                    Copy embed code
                  </Button>
                </div>
              </div>
            </div>
          </div>
        </TabsContent>

        <TabsContent value="customize" className="space-y-4 sm:space-y-6">
          <div className="glass-card p-4 sm:p-6 space-y-5">
            <div>
              <h3 className="font-semibold text-foreground">Customize your Orders Page</h3>
              <p className="text-sm text-muted-foreground">
                These settings control your public ordering page branding and messaging.
              </p>
            </div>

            <div className="grid gap-4 sm:grid-cols-2">
              <div className="space-y-2">
                <Label>Primary color</Label>
                <div className="flex gap-2 sm:gap-3">
                  <Input
                    type="color"
                    value={settings.primaryColor}
                    onChange={(e) => setSettings((p) => ({ ...p, primaryColor: e.target.value }))}
                    className="w-12 h-10 p-1 cursor-pointer"
                  />
                  <Input
                    value={settings.primaryColor}
                    onChange={(e) => setSettings((p) => ({ ...p, primaryColor: e.target.value }))}
                    placeholder="#2563EB"
                    className="flex-1"
                  />
                </div>
                <p className="text-xs text-muted-foreground">Used for buttons, progress, and highlights.</p>
              </div>
              <div className="space-y-2">
                <Label>Logo URL</Label>
                <Input
                  value={settings.logoUrl}
                  onChange={(e) => setSettings((p) => ({ ...p, logoUrl: e.target.value }))}
                  placeholder="https://..."
                />
                <p className="text-xs text-muted-foreground">Displayed at the top of the page.</p>
              </div>
            </div>

            <div className="space-y-2">
              <Label>
                Welcome message <span className="text-muted-foreground">(shown under your business name)</span>
              </Label>
              <Textarea
                value={settings.welcomeMessage}
                onChange={(e) => setSettings((p) => ({ ...p, welcomeMessage: e.target.value }))}
                rows={3}
                placeholder="e.g. Fresh meals made daily. Order online for pickup or delivery."
              />
            </div>

            <div className="space-y-2">
              <Label>
                Order policy <span className="text-muted-foreground">(optional)</span>
              </Label>
              <Textarea
                value={settings.orderPolicy}
                onChange={(e) => setSettings((p) => ({ ...p, orderPolicy: e.target.value }))}
                rows={3}
                placeholder="e.g. Orders are prepared within 45 minutes. Delivery time depends on location."
              />
            </div>

            <div className="space-y-2">
              <Label className="flex items-center gap-2">
                <FileText className="h-4 w-4 text-muted-foreground" />
                Terms & conditions <span className="text-muted-foreground">(optional)</span>
              </Label>
              <Textarea
                value={settings.termsAndConditions}
                onChange={(e) => setSettings((p) => ({ ...p, termsAndConditions: e.target.value }))}
                rows={5}
                placeholder="Add any terms you want customers to know before placing an order."
              />
            </div>

            <div className="flex flex-col sm:flex-row gap-3 sm:items-center sm:justify-end pt-2 border-t border-border">
              <Button onClick={handleSaveChanges} disabled={isSaving} className="w-full sm:w-auto">
                {isSaving ? "Saving..." : "Save changes"}
              </Button>
            </div>
          </div>
        </TabsContent>
      </Tabs>
    </AppShell>
  );
}

